import { supabase, withAuthRetry } from './supabase';
import { logger } from './logger';

export type SubscriptionPlan = 'free' | 'premium';

export interface AdminSubscription {
  id: string;
  employerId: string;
  companyName: string;
  plan: SubscriptionPlan;
  status: string;
  paymentMethod: string | null;
  amountHt: number | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
}

export async function fetchSubscriptions(): Promise<AdminSubscription[]> {
  const { data, error } = await withAuthRetry(async () => await supabase
    .from('subscriptions')
    .select(`
      id,
      employer_id,
      plan,
      status,
      payment_method,
      amount_ht,
      current_period_start,
      current_period_end,
      cancel_at_period_end,
      created_at,
      employer_profiles!subscriptions_employer_id_fkey ( company_name )
    `)
    .order('created_at', { ascending: false }));

  if (error) {
    logger.error('fetch_subscriptions', 'subscriptionService', { error: error.message });
    throw error;
  }
  logger.technical('fetch_subscriptions_ok', 'subscriptionService', { count: (data || []).length });

  return (data || []).map((s: any) => ({
    id: s.id,
    employerId: s.employer_id,
    companyName: s.employer_profiles?.company_name || '',
    plan: s.plan === 'premium' ? 'premium' : 'free',
    status: s.status || 'unknown',
    paymentMethod: s.payment_method,
    amountHt: s.amount_ht,
    currentPeriodStart: s.current_period_start ? new Date(s.current_period_start).toLocaleDateString('fr-FR') : null,
    currentPeriodEnd: s.current_period_end ? new Date(s.current_period_end).toLocaleDateString('fr-FR') : null,
    cancelAtPeriodEnd: s.cancel_at_period_end || false,
    createdAt: new Date(s.created_at).toLocaleDateString('fr-FR'),
  }));
}

/**
 * Bascule un recruteur en premium. On écrit sur la table subscriptions : le
 * trigger SQL propage ensuite employer_profiles.subscription_active.
 */
export async function activateSubscription(employerId: string): Promise<void> {
  logger.functional('activate_subscription', 'subscriptionService', { employerId });

  const { data: existing, error: readError } = await withAuthRetry(async () => await supabase
    .from('subscriptions')
    .select('id')
    .eq('employer_id', employerId)
    .eq('plan', 'premium')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle());

  if (readError) {
    logger.error('activate_subscription', 'subscriptionService', { employerId, error: readError.message });
    throw readError;
  }

  const { error } = existing
    ? await withAuthRetry(async () => await supabase
        .from('subscriptions')
        .update({ status: 'active', cancel_at_period_end: false })
        .eq('id', existing.id))
    : await withAuthRetry(async () => await supabase
        .from('subscriptions')
        .insert({ employer_id: employerId, plan: 'premium', status: 'active' }));

  if (error) {
    logger.error('activate_subscription', 'subscriptionService', { employerId, error: error.message });
    throw error;
  }

  // La ligne gratuite d'origine devient caduque tant que le premium est actif.
  await withAuthRetry(async () => await supabase
    .from('subscriptions')
    .delete()
    .eq('employer_id', employerId)
    .eq('plan', 'free'));

  logger.technical('activate_subscription_ok', 'subscriptionService', { employerId });
}

/**
 * Résilie le premium et rouvre la ligne gratuite, pour que le recruteur reste
 * visible dans le suivi admin.
 */
export async function deactivateSubscription(employerId: string): Promise<void> {
  logger.functional('deactivate_subscription', 'subscriptionService', { employerId });

  const { error } = await withAuthRetry(async () => await supabase
    .from('subscriptions')
    .update({ status: 'cancelled' })
    .eq('employer_id', employerId)
    .eq('plan', 'premium'));

  if (error) {
    logger.error('deactivate_subscription', 'subscriptionService', { employerId, error: error.message });
    throw error;
  }

  const { data: freeRow } = await withAuthRetry(async () => await supabase
    .from('subscriptions')
    .select('id')
    .eq('employer_id', employerId)
    .eq('plan', 'free')
    .limit(1)
    .maybeSingle());

  if (!freeRow) {
    await withAuthRetry(async () => await supabase
      .from('subscriptions')
      .insert({ employer_id: employerId, plan: 'free', status: 'active', amount_ht: 0 }));
  }

  logger.technical('deactivate_subscription_ok', 'subscriptionService', { employerId });
}
